import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { materializeInvestorContacts } from 'data/investors';
import { toInvestorContactRequest } from 'data/investors/investorUtils';
import { selectProfile, selectIsClickToDialLoaded, openClickToDial } from 'data/user';
import { isPropStream } from 'utils/brand';
import { isRedactedNumber } from 'utils/contactUtil';

// click-to-dial on an investor's phone numbers, shared by the search
// card (Results/InvestorCard) and the Investor Profile's Contact Information
// (Profile/ContactInfo). Wired like Contacts/ContactProfile's `dialPhone` --
// same user-type + brand + loaded gate -- and the "Ready to Call" modal itself
// is the shared <ClickToDial> each host screen mounts (InvestorOutreach/index.js,
// Contacts/ContactsList). Returns undefined when dialing isn't available, so
// callers can pass it straight through as ContactPerson's `onPhoneClick`.
//
// ClickToDial is keyed on a propstream-be contact id. `knownContactId` (the
// Profile's resolved one) or the investor's savedGroups may already have it.
// Otherwise materialize is the lookup: it dedupes on the BatchData investor
// id, and the phones shown ARE that contact's own rows (search and profile
// swap in propstream-be's contacts by the same key), so for a number on
// screen it returns the existing contact without writing. Unlike
// resolve-contacts, it also covers skip-traced investors never saved to a
// list. Resolved once per investor, one request at a time. The cache is keyed
// by investor id: PopupHolder renders popups without a key, so a Profile
// opened in place of another reuses this hook's refs.
const useDialInvestorPhone = (investor, knownContactId) => {
  const dispatch = useDispatch();
  const profile = useSelector(selectProfile);
  const isClickToDialLoaded = useSelector(selectIsClickToDialLoaded);
  const resolvedRef = useRef(null); // { investorId, contactId }
  const resolvingContactRef = useRef(false);
  const investorIdRef = useRef(investor.id);
  investorIdRef.current = investor.id;

  const canDial =
    isClickToDialLoaded &&
    isPropStream() &&
    ['FULL', 'TEAM_FULL', 'TEAM'].includes(profile?.get?.('type'));
  if (!canDial) return undefined;

  return (phone) => {
    if (!phone?.number || isRedactedNumber(phone.number)) return;
    if (resolvingContactRef.current) return;

    const open = (contactId) =>
      dispatch(
        openClickToDial({
          contactId,
          phonenumber: String(phone.number).replace(/\D/g, ''),
          // contact_phones.dnc -- the same flag click-to-dial-data returns as
          // myDNC. Public DNC / litigator aren't on the investor payload, so
          // ClickToDial takes them from click-to-dial-data.
          myDnc: phone.dnc === true,
        })
      );

    const contactId =
      knownContactId ??
      (investor.savedGroups || []).find((group) => group.contactId != null)?.contactId ??
      (resolvedRef.current?.investorId === investor.id ? resolvedRef.current.contactId : null);
    if (contactId != null) {
      open(contactId);
      return;
    }

    resolvingContactRef.current = true;
    dispatch(
      materializeInvestorContacts(
        { investors: [toInvestorContactRequest(investor)] },
        ({ response }) => {
          resolvingContactRef.current = false;
          const resolved = response?.results?.[0]?.contactId;
          if (resolved == null) {
            console.warn('Could not resolve a contact id to dial investor', investor.id);
            return;
          }
          resolvedRef.current = { investorId: investor.id, contactId: resolved };
          // Swapped to another investor while this was in flight: don't dial.
          if (investorIdRef.current === investor.id) open(resolved);
        },
        () => {
          resolvingContactRef.current = false;
          console.warn('Could not resolve a contact id to dial investor', investor.id);
        }
      )
    );
  };
};

export default useDialInvestorPhone;
